// src/components/AoiToolbar.jsx
import { useState, useEffect } from "react";
import {
    Pencil,
    Square,
    Circle,
    Move,
    RotateCcw,
    Expand,
    Spline,
    X,
    Check,
} from "lucide-react";

import { useAoiStore } from "../stores/useAoiStore";
import { useUserLayersStore } from "../stores/useUserLayersStore";

export default function AoiToolbar({ draw, version }) {
    const [mode, setMode] = useState(null); // polygon | rectangle | circle | move | rotate | scale | vertex

    const {
        aois,
        activeAoiId,
        aoiPolygonEditMode,
        updateAoiGeometry,
        setActiveAoi,
        enterAoiPolygonEdit,
        exitAoiPolygonEdit,
        setDirtyAoi,
        clearDirtyAoi,
    } = useAoiStore();

    // Day 9 Path A — dirty tracking via onUpdate compare against the
    // snapshot taken at edit-mode entry. Hooked here because AoiToolbar
    // is the single consumer that knows when an edit cycle starts (an
    // AOI is selected as activeAoiId). Per spec: ONLY actual geometry
    // changes flip dirty — tool activation alone doesn't.
    //
    // MlDraw fires onUpdate for every geometry-change gesture:
    //   - add(feature)           line 345  (we use this to seed snapshot)
    //   - delete()               line 354
    //   - _commit(feature)       line 582  (polygon/rect/circle complete)
    //   - _onHandleUp            line 945  (transform + vertex drag end)
    //   - _onVertexContextMenu   line 1028 (vertex remove)
    //   - _onVertexEditLineClick line 1065 (vertex insert)
    //
    // Compare-vs-snapshot naturally skips the false positives from
    // add()/delete() during selection load — those produce a
    // lastFeature.geometry that matches the snapshot exactly.
    useEffect(() => {
        if (!draw || !activeAoiId) return;

        const aoi = aois.find((a) => a.id === activeAoiId);
        const initialGeomText = aoi?.geom ? JSON.stringify(aoi.geom) : "";

        const prevOnUpdate = draw.onUpdate;
        draw.onUpdate = function dirtyAwareOnUpdate() {
            if (typeof prevOnUpdate === "function") prevOnUpdate();
            const currentGeom = draw.lastFeature?.geometry;
            const currentText = currentGeom ? JSON.stringify(currentGeom) : "";
            if (!currentText) {
                // Edge case: lastFeature was cleared (delete()). No edit
                // to be dirty about.
                clearDirtyAoi();
                return;
            }
            if (currentText !== initialGeomText) {
                setDirtyAoi(activeAoiId);
            } else {
                // Geometry matches initial — explicitly clear so an
                // undo-back-to-original goes back to non-dirty.
                clearDirtyAoi();
            }
        };

        return () => {
            // Restore prior onUpdate so we don't accumulate handlers
            // across AOI swaps.
            draw.onUpdate = prevOnUpdate;
        };
        // aois is intentionally NOT a dep — the snapshot must come from
        // the AOI's persisted geom at entry, not from any in-flight
        // local updates. activeAoiId change is the entry signal.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draw, activeAoiId, setDirtyAoi, clearDirtyAoi]);

    // Day 9 — vertex-edit toggle. Mirrors the AoiPanel row-level Spline
    // icon; either entry point flips the same store flag. MapView's
    // effect drives MlDraw's vertexEditMode based on the flag.
    const isVertexActive = Boolean(activeAoiId) && aoiPolygonEditMode === activeAoiId;
    // Keep local `mode` UI hint in sync with the store flag so clicking
    // Vertex from either entry point lights the same button.
    useEffect(() => {
        if (isVertexActive) setMode("vertex");
        else if (mode === "vertex") setMode(null);
        // intentionally not depending on `mode` — avoids ping-pong
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVertexActive]);

    const activateVertex = () => {
        if (!draw || !activeAoiId) return;
        if (isVertexActive) {
            exitAoiPolygonEdit();
        } else {
            draw.stopDrawing?.();
            enterAoiPolygonEdit(activeAoiId);
        }
    };

    const toolbarEnabled = Boolean(activeAoiId);

    const selected = aois.find((a) => a.id === activeAoiId);
    const hasGeom = Boolean(selected?.geom || selected?.geometry);

    // For now: all draw/edit tools are enabled whenever an AOI is selected.
    // (You can re-introduce the hasGeom gating later if you really want.)
    const drawDisabled = !toolbarEnabled;
    const editDisabled = !toolbarEnabled;

    // Save is enabled when an AOI is selected *and* draw has a feature
    const saveDisabled = !toolbarEnabled || !draw?.lastFeature;

    const saveDrawnLayer = useUserLayersStore((s) => s.saveDrawnLayer);

    // -------------------------------------------
    // Auto-select Scale after a commit/transform
    // -------------------------------------------
    useEffect(() => {
        if (!draw) return;
        if (draw.lastFeature) {
            setMode((prev) =>
                prev === null ||
                    prev === "polygon" ||
                    prev === "rectangle" ||
                    prev === "circle"
                    ? "scale"
                    : prev
            );
        }
    }, [version, draw]);

    // -------------------------------------------
    // DRAWING MODES
    // -------------------------------------------
    const activateDraw = (tool) => {
        if (!draw || drawDisabled) return;

        draw.transform = null;
        draw.stopDrawing?.();
        draw.setHandleMode?.("none");

        switch (tool) {
            case "polygon":
                draw.startPolygon();
                break;
            case "rectangle":
                draw.startRectangle();
                break;
            case "circle":
                draw.startCircle();
                break;
            default:
                break;
        }
        setMode(tool);
    };

    // HANDLE MODES. Day 9 — bbox transforms exit vertex-edit if active
    // (mutually exclusive at the MlDraw layer; same intent at the toolbar
    // UI layer so the highlighted button matches what's drawn).
    const activateMove = () => {
        if (!draw || editDisabled) return;
        draw.stopDrawing?.();
        if (isVertexActive) exitAoiPolygonEdit();
        setMode("move");
    };

    const activateScale = () => {
        if (!draw || editDisabled) return;
        draw.stopDrawing?.();
        if (isVertexActive) exitAoiPolygonEdit();
        setMode("scale");
    };

    const activateRotate = () => {
        if (!draw || editDisabled) return;
        draw.stopDrawing?.();
        if (isVertexActive) exitAoiPolygonEdit();
        setMode("rotate");
    };

    const resetToolbar = () => {
        setMode(null);
        draw?.setHandleMode?.("none");
        draw?.stopDrawing?.();
    };

    // CANCEL – discard current AOI geometry & clear selection
    const cancel = () => {
        if (!draw) return;

        // Day 9 — exit vertex-edit if active so the next selection starts
        // fresh with bbox transform handles (the default).
        if (isVertexActive) exitAoiPolygonEdit();

        draw.stopDrawing?.(); // clear preview + cursor
        draw.delete?.();      // clear final AOI + handles
        draw.lastFeature = null;

        setActiveAoi(null);
        resetToolbar();
    };

    // SAVE – persist geometry and clear edit state
    const save = async () => {
        if (!draw || saveDisabled || !draw.lastFeature || !activeAoiId) return;

        // Persist AOI geometry to the aois table
        await updateAoiGeometry(activeAoiId, draw.lastFeature);

        // Also save as a user layer in My Layers catalog
        const aoiName = aois.find(a => a.id === activeAoiId)?.name ?? "Drawn AOI";
        try {
            await saveDrawnLayer(draw.lastFeature, aoiName);
        } catch (err) {
            console.warn("[AoiToolbar] saveDrawnLayer failed:", err);
        }

        // Day 9 — exit vertex-edit if active so the next selection reverts
        // to bbox transforms (consistent default).
        if (isVertexActive) exitAoiPolygonEdit();

        // Clear draw state
        draw._clearHandles?.();
        draw.stopDrawing?.();
        draw.lastFeature = null;
        draw.currentMode = null;

        setMode(null);
        setActiveAoi(null);
    };

    // -------------------------------------------
    // UI HELPERS
    // -------------------------------------------
    const isActive = (t) => toolbarEnabled && mode === t;

    const buttonBase =
        "group relative flex items-center justify-center " +
        "w-[26px] h-[26px] rounded-md bg-bg-softer " +
        "border border-[#1a1d24] hover:bg-bg-soft " +
        "transition";

    const activeButton =
        "border-aoi-neon shadow-[0_0_8px_2px_#16f2d4]";

    const iconActive =
        "text-aoi-neon animate-aoiPulseIcon drop-shadow-[0_0_4px_#16f2d4]";

    const iconInactive = "text-gray-300";

    const NeonBar = () => (
        <div className="absolute bottom-0 left-0 w-full h-[2px] bg-aoi-neon animate-aoiPulse shadow-[0_0_8px_3px_#16f2d4]" />
    );

    const Tooltip = ({ label }) => (
        <div className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-1 
                        bg-bg-soft text-gray-200 text-[10px] 
                        border border-[#1a1d24] rounded 
                        opacity-0 group-hover:opacity-100 transition
                        pointer-events-none whitespace-nowrap 
                        shadow-[0_0_6px_1px_#000] animate-aoiFade z-50">
            {label}
        </div>
    );

    return (
        <div
            className={[
                "absolute top-0 left-10 z-30 flex items-center gap-1.5",
                "px-2 py-2 bg-bg-soft/90 backdrop-blur",
                "border border-[#1a1d24] rounded-lg shadow-lg",
            ].join(" ")}
        >
            {/* POLYGON */}
            <button
                disabled={drawDisabled}
                onClick={() => activateDraw("polygon")}
                className={`${buttonBase} ${drawDisabled ? "opacity-40 pointer-events-none" : ""
                    } ${isActive("polygon") ? activeButton : ""}`}
            >
                {/* <Tooltip label="Polygon" /> */}
                <Pencil
                    size={15}
                    className={isActive("polygon") ? iconActive : iconInactive}
                />
                {isActive("polygon") && <NeonBar />}
            </button>

            {/* RECTANGLE */}
            <button
                disabled={drawDisabled}
                onClick={() => activateDraw("rectangle")}
                className={`${buttonBase} ${drawDisabled ? "opacity-40 pointer-events-none" : ""
                    } ${isActive("rectangle") ? activeButton : ""}`}
            >
                {/* <Tooltip label="Rectangle" /> */}
                <Square
                    size={15}
                    className={isActive("rectangle") ? iconActive : iconInactive}
                />
                {isActive("rectangle") && <NeonBar />}
            </button>

            {/* CIRCLE */}
            <button
                disabled={drawDisabled}
                onClick={() => activateDraw("circle")}
                className={`${buttonBase} ${drawDisabled ? "opacity-40 pointer-events-none" : ""
                    } ${isActive("circle") ? activeButton : ""}`}
            >
                {/* <Tooltip label="Circle" /> */}
                <Circle
                    size={15}
                    className={isActive("circle") ? iconActive : iconInactive}
                />
                {isActive("circle") && <NeonBar />}
            </button>

            {/* MOVE */}
            <button
                disabled={editDisabled}
                onClick={activateMove}
                className={`${buttonBase} ${editDisabled ? "opacity-40 pointer-events-none" : ""
                    } ${isActive("move") ? activeButton : ""}`}
            >
                {/* <Tooltip label="Move" /> */}
                <Move
                    size={15}
                    className={isActive("move") ? iconActive : iconInactive}
                />
                {isActive("move") && <NeonBar />}
            </button>

            {/* SCALE */}
            <button
                disabled={editDisabled}
                onClick={activateScale}
                className={`${buttonBase} ${editDisabled ? "opacity-40 pointer-events-none" : ""
                    } ${isActive("scale") ? activeButton : ""}`}
            >
                {/* <Tooltip label="Scale" /> */}
                <Expand
                    size={15}
                    className={isActive("scale") ? iconActive : iconInactive}
                />
                {isActive("scale") && <NeonBar />}
            </button>

            {/* ROTATE */}
            <button
                disabled={editDisabled}
                onClick={activateRotate}
                className={`${buttonBase} ${editDisabled ? "opacity-40 pointer-events-none" : ""
                    } ${isActive("rotate") ? activeButton : ""}`}
            >
                {/* <Tooltip label="Rotate" /> */}
                <RotateCcw
                    size={15}
                    className={isActive("rotate") ? iconActive : iconInactive}
                />
                {isActive("rotate") && <NeonBar />}
            </button>

            {/* VERTEX EDIT — Day 9. Mutually exclusive with bbox transforms
                above. Disabled until an AOI is selected. Icon matches the
                row-level Spline affordance in AoiPanel. */}
            <button
                disabled={editDisabled}
                onClick={activateVertex}
                title="Edit vertices"
                className={`${buttonBase} ${editDisabled ? "opacity-40 pointer-events-none" : ""
                    } ${isVertexActive ? activeButton : ""}`}
            >
                <Spline
                    size={15}
                    className={isVertexActive ? iconActive : iconInactive}
                />
                {isVertexActive && <NeonBar />}
            </button>

            {/* CANCEL */}
            <button
                disabled={!toolbarEnabled}
                onClick={cancel}
                className={`${buttonBase} ${!toolbarEnabled ? "opacity-40 pointer-events-none" : ""
                    }`}
            >
                {/* <Tooltip label="Cancel" /> */}
                <X size={14} className="text-gray-300" />
            </button>

            {/* SAVE */}
            <button
                disabled={saveDisabled}
                onClick={save}
                className={
                    saveDisabled
                        ? `${buttonBase} opacity-30`
                        : `${buttonBase} border-aoi-neon shadow-[0_0_8px_2px_#16f2d4]`
                }
            >
                {/* <Tooltip label="Save" /> */}
                <Check
                    size={14}
                    className={
                        !saveDisabled
                            ? "text-aoi-neon drop-shadow-[0_0_6px_#16f2d4]"
                            : "text-fg-secondary"
                    }
                />
                {!saveDisabled && <NeonBar />}
            </button>
        </div>
    );
}